import { getComparison, getPlayerOptions } from "actions/players"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { selectCompare, selectSearchOptions1, selectSearchOptions2 } from "selectors/players"

import { DetailsList, ComboBox } from "@fluentui/react"
import { debounce } from "lodash"
import StatCard from "./shared/StatCard"

import './Compare.scss'

const defaultColumns = [
  {
    key: 'matchNumber',
    name: '#',
    minWidth: 50,
    maxWidth: 50,
    fieldName: 'match_number',
    isResizable: false,
  },
  {
    key: 'event',
    name: 'Event',
    minWidth: 125,
    fieldName: 'event',
  },
  {
    key: 'edition',
    name: 'Edition',
    minWidth: 125,
    maxWidth: 125,
    fieldName: 'edition',
    isResizable: true,
  },
  {
    key: 'roundGame',
    name: 'Round/Game',
    minWidth: 100,
    maxWidth: 100,
    fieldName: 'round_game',
    isResizable: true,
  },
  {
    key: 'restreamerLocation',
    name: 'Restreamer/Location',
    minWidth: 200,
    maxWidth: 200,
    fieldName: 'restreamer_location',
    isResizable: true,
  },
  {
    key: 'matchType',
    name: 'Type',
    minWidth: 60,
    maxWidth: 80,
    fieldName: 'match_type',
    isResizable: true,
  },
  {
    key: 'date',
    name: 'Date',
    minWidth: 100,
    maxWidth: 100,
    fieldName: 'date_time',
    isResizable: false,
  },
  {
    key: 'player1',
    name: 'Player 1',
    minWidth: 150,
    maxWidth: 150,
    fieldName: "player1_name",
    isResizable: true,
  },
  {
    key: 'player1Score',
    name: 'Score',
    minWidth: 50,
    maxWidth: 50,
    fieldName: "player1_score",
    isResizable: true,
  },
  {
    key: 'player2',
    name: 'Player 2',
    minWidth: 150,
    maxWidth: 150,
    fieldName: "player2_name",
    isResizable: true,
  },
  {
    key: 'player2Score',
    name: 'Score',
    minWidth: 50,
    maxWidth: 50,
    fieldName: "player2_score",
    isResizable: true,
  },
]

const Compare = () => {
  const dispatch = useDispatch()
  const compare = useSelector(selectCompare)
  const searchOptions1 = useSelector(selectSearchOptions1) || []
  const searchOptions2 = useSelector(selectSearchOptions2) || []
  const combo1Ref = useRef(null)
  const combo2Ref = useRef(null)
  const [player1, setPlayer1] = useState(null)
  const [player2, setPlayer2] = useState(null)
  const [displayOptions1, setDisplayOptions1] = useState([])
  const [displayOptions2, setDisplayOptions2] = useState([])

  useEffect(() => {
    if (searchOptions1.length > 0) setDisplayOptions1(searchOptions1)
  }, [searchOptions1])

  useEffect(() => {
    if (searchOptions2.length > 0) setDisplayOptions2(searchOptions2)
  }, [searchOptions2])

  const fetchPlayers1 = useCallback(debounce((filter) => {
    dispatch(getPlayerOptions(filter, true))
  }, 300), [dispatch])

  const fetchPlayers2 = useCallback(debounce((filter) => {
    dispatch(getPlayerOptions(filter, false))
  }, 300), [dispatch])

  useEffect(() => {
    return () => {
      fetchPlayers1.cancel()
      fetchPlayers2.cancel()
    }
  }, [fetchPlayers1, fetchPlayers2])

  const handleInputChange1 = (text) => {
    fetchPlayers1(text)
    if (text) combo1Ref.current?.focus(true)
  }

  const handleInputChange2 = (text) => {
    fetchPlayers2(text)
    if (text) combo2Ref.current?.focus(true)
  }

  useEffect(() => {
    if (player1 && player2) {
      dispatch(getComparison(player1.key, player2.key))
    }
  }, [dispatch, player1, player2])

  const onChangePlayer1 = (_, option) => {
    if (option) setPlayer1(option)
  }

  const onChangePlayer2 = (_, option) => {
    if (option) setPlayer2(option)
  }

  const fmtOdds = (val) => val != null && !isNaN(val) ? `${(val * 100).toFixed(1)}%` : '—'
  const player1Percent = fmtOdds(compare?.odds?.player1_odds)
  const player2Percent = fmtOdds(compare?.odds?.player2_odds)

  return (
    <div className="compare">
      <div className="playerCompare">
        <div className="player">
          <ComboBox
            componentRef={combo1Ref}
            label="Player 1"
            placeholder="Select or search a player"
            allowFreeform
            autoComplete="off"
            openMenuOnClick
            selectedKey={player1?.key ?? null}
            options={displayOptions1}
            onChange={onChangePlayer1}
            onInputValueChange={handleInputChange1}
          />
          <div className="playerTitle">{compare?.player1?.name}</div>
          <StatCard title="Elo" value={compare?.player1?.current_elo ?? '—'} />
          <StatCard title="Overall W-L" value={`${compare?.player1?.wins} - ${compare?.player1?.losses}`} />
          <StatCard title="Win chance" value={player1Percent} />
          <h5>Last played: {compare?.player1?.last_match_date ? new Date(compare.player1.last_match_date).toLocaleDateString() : '—'}</h5>
        </div>
        <div className="player">
          <ComboBox
              componentRef={combo2Ref}
              label="Player 2"
              placeholder="Select or search a player"
              allowFreeform
              autoComplete="off"
              openMenuOnClick
              selectedKey={player2?.key ?? null}
              options={displayOptions2}
              onChange={onChangePlayer2}
              onInputValueChange={handleInputChange2}
            />
          <div className="playerTitle">{compare?.player2?.name}</div>
          <StatCard title="Elo" value={compare?.player2?.current_elo ?? '—'} />
          <StatCard title="Overall W-L" value={`${compare?.player2?.wins} - ${compare?.player2?.losses}`} />
          <StatCard title="Win chance" value={player2Percent} />
          <h5>Last played: {compare?.player2?.last_match_date ? new Date(compare.player2.last_match_date).toLocaleDateString() : '—'}</h5>
        </div>
      </div>
      <div className="matchTable" data-is-scrollable={true}>
        <h1>MATCH HISTORY</h1>
        <div style={{ minWidth: 1100 }}>
          <DetailsList
            items={compare?.match_history || []}
            columns={defaultColumns}
            getKey={(item) => item.id}
            setKey="none"
            selectionMode="none"
            checkboxVisibility={2}
          />
        </div>
      </div>
    </div>
  )
}

export default Compare
